/*
 * ATBT product page: information tabs, details close-up, product recommendations and "Buy now".
 * Loaded (defer) by sections/atbt-product-info.liquid and sections/atbt-product-recommendations.liquid;
 * runs once even when both include it. Plain ES2018, no dependencies.
 *
 * Delegated hooks (document level, so they survive theme-editor section reloads):
 *   [data-open-tab="details|delivery|reviews"]  open that tab of the info section, focus it and scroll to it
 *     (aliases: [data-pdp-open-panel], used by sections/atbt-product-main.liquid, and the design's [data-open-panel])
 *   a[href="#reviews"], "#delivery", "#returns", "#shipping", "#details"   same (also on load / hashchange)
 *   a[href="#some-id"] whose target sits in a hidden tab panel (e.g. #looxReviews)  opens that tab, scrolls to it
 *   document event  'atbt:open-tab'  { detail: { tab: 'delivery', focus: false, scroll: true } }
 *   window.ATBT.openProductTab(name, { focus, scroll })  -> true when the tab exists
 *   [data-atbt-buy="handle"]  window.ATBT.quickView(handle) when available, else the link is followed
 * Owned elsewhere: [data-open-model-help] / [data-open-size-guide] are handled by assets/atbt-product.js; when no
 *   dialog opened during the click, this file presses the main section's own [data-pdp-open="model-help|size-guide"].
 */
(function () {
  'use strict';

  if (window.__atbtProductInfo) return;
  window.__atbtProductInfo = true;

  var doc = document;
  var reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var HASH_TABS = {
    details: 'details',
    'panel-details': 'details',
    delivery: 'delivery',
    'panel-delivery': 'delivery',
    returns: 'delivery',
    shipping: 'delivery',
    reviews: 'reviews',
    'panel-reviews': 'reviews'
  };

  function prefersReducedMotion() {
    return Boolean(reducedMotion && reducedMotion.matches);
  }

  /* ------------------------------------------------------------------ scrolling */

  function headerOffset() {
    var header = doc.getElementById('shopify-section-atbt-header');
    if (!header) return 0;
    var style = window.getComputedStyle(header);
    if (style.position !== 'sticky' && style.position !== 'fixed') return 0;
    var rect = header.getBoundingClientRect();
    return rect.bottom > 0 ? Math.min(rect.height, 200) : 0;
  }

  function scrollToElement(el) {
    if (!el) return;
    var top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset() - 16;
    try {
      window.scrollTo({ top: Math.max(0, top), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    } catch (error) {
      window.scrollTo(0, Math.max(0, top));
    }
  }

  /* ----------------------------------------------------------------------- tabs */

  function tabsOf(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[role="tab"][data-panel]'));
  }

  function panelFor(root, name) {
    return root.querySelector('[data-tab-panel="' + name + '"]');
  }

  function selectTab(root, name, options) {
    var opts = options || {};
    var tabs = tabsOf(root);
    var target = null;

    tabs.forEach(function (tab) {
      if (tab.getAttribute('data-panel') === name) target = tab;
    });
    if (!target) return false;

    tabs.forEach(function (tab) {
      var active = tab === target;
      var panel = panelFor(root, tab.getAttribute('data-panel'));
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
      if (panel) panel.hidden = !active;
    });

    if (opts.focus) {
      try {
        target.focus({ preventScroll: true });
      } catch (error) {
        target.focus();
      }
    }
    if (opts.scroll) scrollToElement(root);
    return true;
  }

  function openProductTab(name, options) {
    var root = doc.querySelector('[data-atbt-info]');
    if (!root || !name) return false;
    return selectTab(root, String(name), options);
  }

  function onTabKeydown(event) {
    var tab = event.target.closest && event.target.closest('[data-atbt-info] [role="tab"][data-panel]');
    if (!tab) return;
    var key = event.key;
    if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Home' && key !== 'End') return;

    var root = tab.closest('[data-atbt-info]');
    var tabs = tabsOf(root);
    var index = tabs.indexOf(tab);
    if (index < 0) return;
    event.preventDefault();

    var next;
    if (key === 'Home') next = 0;
    else if (key === 'End') next = tabs.length - 1;
    else next = (index + (key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    selectTab(root, tabs[next].getAttribute('data-panel'), { focus: true });
  }

  function tabFromHash(hash) {
    var key = String(hash || '').replace(/^#/, '');
    var lower = key.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(HASH_TABS, lower)) return { name: HASH_TABS[lower], target: null };
    // An anchor inside a hidden panel (e.g. a review app's #looxReviews): open its tab, then scroll to it.
    var target = null;
    try {
      target = key ? doc.getElementById(decodeURIComponent(key)) : null;
    } catch (error) {
      target = null;
    }
    var panel = target && target.closest ? target.closest('[data-atbt-info] [data-tab-panel]') : null;
    return panel ? { name: panel.getAttribute('data-tab-panel'), target: target } : null;
  }

  function openFromHash(hash, focus) {
    var match = tabFromHash(hash);
    if (!match) return false;
    if (!openProductTab(match.name, { focus: focus, scroll: !match.target })) return false;
    if (match.target) scrollToElement(match.target);
    return true;
  }

  function openFromLocation() {
    openFromHash(window.location.hash, false);
  }

  /* --------------------------------------------------------- details close-up */

  var bodyOverflow = '';

  function openZoom(trigger) {
    var root = trigger.closest('[data-atbt-info]');
    var dialog = root && root.querySelector('dialog[data-atbt-zoom]');
    if (!dialog) return;
    dialog.atbtTrigger = trigger;
    bodyOverflow = doc.body.style.overflow;
    doc.body.style.overflow = 'hidden';
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    var closer = dialog.querySelector('[data-atbt-zoom-close]');
    if (closer) closer.focus();
  }

  function closeZoom(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === 'function') {
      if (dialog.open) dialog.close();
    } else {
      dialog.removeAttribute('open');
      afterZoomClose(dialog);
    }
  }

  function afterZoomClose(dialog) {
    doc.body.style.overflow = bodyOverflow;
    var trigger = dialog.atbtTrigger;
    dialog.atbtTrigger = null;
    if (trigger && doc.contains(trigger)) {
      try {
        trigger.focus({ preventScroll: true });
      } catch (error) {
        trigger.focus();
      }
    }
  }

  /* ------------------------------------------------------------ recommendations */

  function hideRecs(el) {
    el.hidden = true;
  }

  function loadRecs(el) {
    var url = el.getAttribute('data-url');
    if (!url || typeof window.fetch !== 'function') return;
    window
      .fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-atbt-recs]');
        if (!fresh || !fresh.querySelector('.featured-card')) {
          hideRecs(el);
          return;
        }
        el.innerHTML = fresh.innerHTML;
        if (fresh.hasAttribute('aria-labelledby')) {
          el.setAttribute('aria-labelledby', fresh.getAttribute('aria-labelledby'));
        }
        el.hidden = false;
      })
      .catch(function () {
        hideRecs(el);
      });
  }

  function initRecs(scope) {
    var list = (scope || doc).querySelectorAll('[data-atbt-recs]');
    Array.prototype.forEach.call(list, function (el) {
      if (el.getAttribute('data-atbt-recs-init') === 'true') return;
      el.setAttribute('data-atbt-recs-init', 'true');
      if (el.querySelector('.featured-card')) {
        el.hidden = false;
        return;
      }
      if (!('IntersectionObserver' in window)) {
        loadRecs(el);
        return;
      }
      // A hidden element never intersects, so observe its visible wrapper.
      var watch = el.parentElement || el;
      var observer = new IntersectionObserver(
        function (entries) {
          if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
          observer.disconnect();
          loadRecs(el);
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      observer.observe(watch);
    });
  }

  /* -------------------------------------------------------------------- events */

  doc.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var tab = target.closest('[data-atbt-info] [role="tab"][data-panel]');
    if (tab) {
      selectTab(tab.closest('[data-atbt-info]'), tab.getAttribute('data-panel'));
      return;
    }

    var opener = target.closest('[data-open-tab], [data-pdp-open-panel], [data-open-panel]');
    if (opener) {
      var tabName = opener.getAttribute('data-open-tab') || opener.getAttribute('data-pdp-open-panel') || opener.getAttribute('data-open-panel');
      if (openProductTab(tabName, { focus: true, scroll: true })) event.preventDefault();
      return;
    }

    // "Find your iPhone model" / "Compare the sizes" in the Details tab. assets/atbt-product.js listens for these
    // globally; if no dialog opened by the end of this click, press the main section's own trigger instead.
    var helper = target.closest('[data-open-model-help], [data-open-size-guide]');
    if (helper) {
      var kind = helper.hasAttribute('data-open-model-help') ? 'model-help' : 'size-guide';
      window.setTimeout(function () {
        if (doc.querySelector('dialog[open]')) return;
        var own = doc.querySelector('[data-pdp-open="' + kind + '"]');
        if (own && own !== helper) own.click();
      }, 0);
      return;
    }

    var zoomOpen = target.closest('[data-atbt-zoom-open]');
    if (zoomOpen) {
      event.preventDefault();
      openZoom(zoomOpen);
      return;
    }

    var zoomClose = target.closest('[data-atbt-zoom-close]');
    if (zoomClose) {
      closeZoom(zoomClose.closest('dialog'));
      return;
    }

    if (target.matches && target.matches('dialog[data-atbt-zoom]')) {
      // Click on the backdrop (outside the dialog box) closes it.
      var rect = target.getBoundingClientRect();
      var outside = event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
      if (outside) closeZoom(target);
      return;
    }

    var buy = target.closest('[data-atbt-buy]');
    if (buy) {
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var atbt = window.ATBT;
      var handle = buy.getAttribute('data-atbt-buy');
      if (!handle || !atbt || typeof atbt.quickView !== 'function') return;
      event.preventDefault();
      var card = buy.closest('.featured-card');
      var caption = card && card.querySelector('.card-caption');
      atbt.quickView(handle, { trigger: buy, category: caption ? caption.textContent.trim() : '' });
      return;
    }

    var link = target.closest('a[href^="#"]');
    if (link && link.getAttribute('href').length > 1 && openFromHash(link.getAttribute('href'), true)) {
      event.preventDefault();
    }
  });

  doc.addEventListener('keydown', onTabKeydown);

  // 'close' does not bubble: listen in the capture phase.
  doc.addEventListener(
    'close',
    function (event) {
      var dialog = event.target;
      if (dialog && dialog.matches && dialog.matches('dialog[data-atbt-zoom]')) afterZoomClose(dialog);
    },
    true
  );

  doc.addEventListener('atbt:open-tab', function (event) {
    var detail = event.detail || {};
    openProductTab(detail.tab, { focus: Boolean(detail.focus), scroll: detail.scroll !== false });
  });

  window.addEventListener('hashchange', openFromLocation);

  /* ------------------------------------------------------------- theme editor */

  doc.addEventListener('shopify:section:load', function (event) {
    initRecs(event.target);
  });

  doc.addEventListener('shopify:block:select', function (event) {
    var block = event.target;
    if (!block || !block.closest) return;
    var panel = block.closest('[data-atbt-info] [data-tab-panel]');
    if (panel) selectTab(panel.closest('[data-atbt-info]'), panel.getAttribute('data-tab-panel'));
    if (block.tagName === 'DETAILS' && block.closest('.faq-items')) block.open = true;
  });

  doc.addEventListener('shopify:block:deselect', function (event) {
    var block = event.target;
    if (block && block.tagName === 'DETAILS' && block.closest && block.closest('.faq-items')) block.open = false;
  });

  /* -------------------------------------------------------------------- start */

  window.ATBT = window.ATBT || {};
  window.ATBT.openProductTab = openProductTab;

  initRecs(doc);
  openFromLocation();
})();
